/**
 * Guards against javascript:/data:/vbscript: URLs reaching window.open or an anchor href.
 *
 * Values that end up in a link or a new tab often come from the API or the Redux store
 * (invoice URIs, incentive landing pages, widget preview links). A hostile value such as
 * `javascript:alert(1)` executes in our origin when passed to window.open, so every such
 * value goes through safeUrl first.
 */

/** Schemes we are willing to hand to the browser. */
const ALLOWED_SCHEME = /^(https?:|mailto:)/i;

/**
 * Protocol-relative prefixes ("//evil.com", or the backslash variant browsers normalise
 * to it) resolve to another origin, so they are not treated as relative paths.
 */
const PROTOCOL_RELATIVE = /^[/\\]{2}/;

/**
 * Returns the URL when it is safe to navigate to, otherwise null.
 * Absolute http(s)/mailto URLs and same-origin relative paths ("/services") are allowed.
 */
export const safeUrl = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const url = raw.trim();
    if (ALLOWED_SCHEME.test(url)) return url;
    if (url.startsWith('/') && !PROTOCOL_RELATIVE.test(url)) return url;
    return null;
};

/**
 * window.open for untrusted values. Opens a new tab with noopener/noreferrer and does
 * nothing when the URL is not safe. Returns whether the URL was accepted.
 */
export const openSafeUrl = (raw: string | null | undefined): boolean => {
    const url = safeUrl(raw);
    if (!url) return false;
    // `url` is the return value of safeUrl above, so it has passed the scheme allowlist and
    // cannot be a javascript:/data: URI here. This is the one audited window.open sink in the
    // app; callers must route through it rather than calling window.open directly.
    // nosemgrep: AIK_js_xss_location
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
};
